import { toInteger } from 'lodash';
import { getFramework } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import { transformTextDataToLowLever } from '@/utils/transform-kit';
import { type ILayerBehavior, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { LayerDataProcessor, PPTSaver, ResultCanvasCreator } from '../utils';

type Behaviors = Pick<ILayerBehavior, 'onBeforeSaveAsync' | 'savePPTLayerAsync' | 'getResultCanvasAsync'>;

export const createSaveBehaviors = (identity: string) => {
    const behaviors: Behaviors = {
        /**
         * 保存前对文本图层数据的额外操作
         *
         * - 该方法会改变 mutable 入参
         *
         * @param layer - 图层属性
         * @param isAutoSave - 是否自动保存
         */
        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave: boolean) {
            const layerData = layer.data as ITextLayerProps;
            deleteExtraInfo(layer);
            // 将 screenshotUrl 转换成 http url
            const { shapeExtension, screenshotUrl } = layerData;
            if (!!shapeExtension && !!screenshotUrl) {
                layerData.screenshotUrl = await base64Kit.getRemoteUrlAsync(screenshotUrl);
            }

            // FIXME
            // 由于 scale 钩子未对 textContents 中的 fontSize 进行取整操作，所以这里进行取整
            // 但理论上应该统一处理
            const { textContents = [] } = layerData;
            if (textContents.length > 0) {
                textContents.forEach((item) => {
                    const { fontSize } = item;
                    Reflect.set(item, 'fontSize', toInteger(fontSize));
                });
            }

            // 经过后处理的 layerData
            const processedLayerData = LayerDataProcessor.postProcess(layerData);

            // 将图层数据转为底层需要的格式
            const foundationLayerData = transformTextDataToLowLever(processedLayerData);
            Reflect.set(layer, 'data', foundationLayerData);
        },

        /**
         * 文本图层出图
         *
         * @param layer - 文本图层
         * @param stageSize - 舞台尺寸
         */
        async getResultCanvasAsync(layer, stageSize) {
            const framework = getFramework(identity);

            // key: fontFamilySubset, 例如 "AlibabaSans-Regular-md5字符串"
            // base: base64 string
            const fontSubsetToBase64: Record<string, string> = {};
            const fontFamilyMap = layer.__runtime?.fontFamilyMap;
            const fontBase64Dict = framework.getFontBase64(layer);

            if (fontFamilyMap) {
                Object.keys(fontFamilyMap).forEach((fontFamily) => {
                    const key = fontFamilyMap[fontFamily];
                    if (fontBase64Dict[fontFamily]) {
                        fontSubsetToBase64[key] = fontBase64Dict[fontFamily];
                    }
                });
            }

            const creator = new ResultCanvasCreator(identity, layer, stageSize, fontSubsetToBase64);
            return creator.create();
        },

        /**
         * 将文本图层转为 ppt 图层写入 slide
         *
         * - 该方法会改变 mutable 入参
         *
         * @param slide - 幻灯片
         * @param layer - 图层属性
         * @param stageWidth - 舞台宽度
         * @param stageHeight - 舞台高度
         * @param watermark - 水印
         * @param outFontFamilyArr - 字体列表
         */
        async savePPTLayerAsync(slide, layer, stageWidth, stageHeight, watermark, outFontFamilyArr) {
            const pptSaver = new PPTSaver(layer);
            pptSaver.save(slide, outFontFamilyArr);
        },
    };

    return behaviors;
};
